import { computed, Injectable, signal } from "@angular/core";
import { UserStateModel } from "../Dto/UserStateModel";
import { UserListItem } from "../Dto/UserModel";
import { PaginationInfo } from "../Dto/UserSearchResponse";
import { SearchUserRequest } from "../Dto/UserSearchRequest";

const initialState: UserStateModel = {
  data: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  },
  searchParams: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

@Injectable({ providedIn: "root" })
export class UserState {
  private readonly state = signal<UserStateModel>(initialState);
  public readonly data = computed(() => this.state().data);
  public readonly pagination = computed(() => this.state().pagination);
  public readonly searchParams = computed(() => this.state().searchParams);
  public readonly loading = computed(() => this.state().loading);
  public readonly error = computed(() => this.state().error);

  setUsers(data: UserListItem[], pagination: PaginationInfo): void {
    this.state.update((state) => ({
      ...state,
      data,
      pagination,
      loading: false,
      error: null,
    }));
  }

  setSearchParams(params: SearchUserRequest): void {
    this.state.update((state) => ({
      ...state,
      searchParams: params,
    }));
  }

  setLoading(loading: boolean): void {
    this.state.update((state) => ({ ...state, loading }));
  }

  setError(error: string | null): void {
    this.state.update((state) => ({ ...state, error }));
  }

  updateUserInState(userId: string, userData: Partial<UserListItem>): void {
    this.state.update((state) => ({
      ...state,
      data: state.data.map((user) =>
        (() => {
          if (user.id === userId) {
            return { ...user, ...userData };
          }
          return user;
        })(),
      ),
      loading: false,
      error: null,
    }));
  }

  addUserToState(user: UserListItem): void {
    this.state.update((state) => ({
      ...state,
      data: [user, ...state.data],
      pagination: {
        ...state.pagination,
        total: state.pagination.total + 1,
      },
      loading: false,
      error: null,
    }));
  }

  removeUserFromState(userId: string): void {
    this.state.update((state) => ({
      ...state,
      data: state.data.filter((user) => user.id !== userId),
      pagination: {
        ...state.pagination,
        total: Math.max(0, state.pagination.total - 1),
      },
      loading: false,
      error: null,
    }));
  }

  userById = computed(() => {
    const users = this.state().data;
    return (id: string) => users.find((user) => user.id === id) || null;
  });
}
